// AGENT-A5: Zustand store for the dashboard rebuild's filter state.
//
// Distinct from the legacy `src/state/filterStore.ts` (cross-filter map). The
// dashboard shell, sidebar (A5), filter bar (A6), view selectors + table (A7),
// and charts (A8) all read/write through this store.
//
// Action signatures are stable contract surface for downstream agents:
//   toggleCategory(name)
//   toggleParent(name)
//   toggleVendor(name)
//   setBoolean(key, value)
//   setNewOrExisting(v)
//   setMode("quotes" | "orders")
//   setDateRange(r)
//   setView("left" | "right", "fy" | "yoy" | "qoq")
//   selectAllCategories(names)
//   clearCategories()
//   reset()
//   toServerShape() -> ServerFilterShape
//
// AGENT-BD: additive — added `pivotVisualId` field and `setPivotVisualId`
// action so the right ViewSelector tab (FY / YoY / QoQ) can swap which HSQ
// pivot visual the metrics table mirrors. The default is the "Last 12 Months"
// pivot id; when the user picks YoY we send the "Calendar Year YoY" pivot id
// on the request body. `toServerShape()` only includes the field when it
// differs from the default — keeps backwards compatibility with older
// fixtures and the legacy contract.

import { create } from "zustand";
import type {
  DashboardMode,
  DashboardView,
  DateRange,
  NewOrExisting,
  ServerFilterShape,
  TriBooleanValue,
} from "../types";

export interface FilterState {
  /* ---------- selections ---------- */
  categories: Set<string>;
  parentProducts: Set<string>;
  /** Specific part numbers picked from the sidebar's third-level dropdown
   *  under each parent product. Serialised as a `Product Table.PART_NUMBER
   *  IN […]` filter on the request body. */
  partNumbers: Set<string>;
  vendors: Set<string>;
  booleans: Record<string, boolean>;
  /** 1Y-online-tri: the "online" boolean is presented as a tri-state in the
   *  UI ("yes" | "no" | "both"). It lives OUTSIDE the `booleans` map so the
   *  other 7 booleans keep their plain boolean type and don't break A6's
   *  BooleanToggle contract. On serialization we project this back into
   *  `booleans.online` (true/false) — and OMIT the key entirely when "both",
   *  so the backend doesn't filter. */
  online: TriBooleanValue;
  newOrExisting: NewOrExisting;
  mode: DashboardMode;
  dateRange: DateRange;
  viewLeft: DashboardView;
  viewRight: DashboardView;
  /** AGENT-A7: FY selection for the LEFT ViewSelector card (count metrics).
   *  Multi-select list of FY tokens (e.g. ["FY26","FY25"]). Empty = no FY
   *  filter applied from this side. Mirrored into `dateRange.years` so the
   *  existing /report path keeps applying the filter unchanged. */
  fyLeft: string[];
  /** AGENT-A7: FY selection for the RIGHT ViewSelector card (unit qty). */
  fyRight: string[];
  /** AGENT-BD: HSQ pivot visual id the metrics table should mirror. Default
   *  is the "Last 12 Months" pivot; ViewSelectors swaps this when the user
   *  picks the YoY (or QoQ) tab. */
  pivotVisualId: string;

  /* ---------- actions ---------- */
  toggleCategory: (name: string) => void;
  toggleParent: (name: string) => void;
  togglePartNumber: (name: string) => void;
  toggleVendor: (name: string) => void;
  /** 1Y-pp-filter: convenience setter that replaces the entire parent-product
   *  selection. The per-item toggle is `toggleParent`. */
  setParentProducts: (names: string[]) => void;
  setBoolean: (key: string, value: boolean) => void;
  /** 1Y-online-tri: setter for the tri-state online filter. */
  setOnline: (v: TriBooleanValue) => void;
  setNewOrExisting: (v: NewOrExisting) => void;
  setMode: (m: DashboardMode) => void;
  setDateRange: (r: DateRange) => void;
  setView: (side: "left" | "right", v: DashboardView) => void;
  /** AGENT-A7: set the FY multi-select list for the LEFT view card. */
  setFyLeft: (years: string[]) => void;
  /** AGENT-A7: set the FY multi-select list for the RIGHT view card. */
  setFyRight: (years: string[]) => void;
  /** AGENT-BD: override which HSQ pivot visual the table mirrors. */
  setPivotVisualId: (id: string) => void;
  selectAllCategories: (names: string[]) => void;
  clearCategories: () => void;
  reset: () => void;

  /* ---------- serialization ---------- */
  toServerShape: () => ServerFilterShape;
}

/** Default pivot visual = HSQ "YoY (Calendar Year)" pivot. The dashboard
 *  lands on the YoY view (the per-(Year, Parent Product) breakdown the
 *  user confirmed they want by default). Mixed-view reconcile then keeps
 *  both ViewSelector cards in sync on this view. */
export const DEFAULT_PIVOT_VISUAL_ID = "532f1cbdb173212cf1be";

const DEFAULT_DATE_RANGE: DateRange = {
  kind: "fy",
  years: [],
};

function toggleInSet<T>(s: Set<T>, item: T): Set<T> {
  const next = new Set(s);
  if (next.has(item)) next.delete(item);
  else next.add(item);
  return next;
}

export const useFilterStore = create<FilterState>((set, get) => ({
  categories: new Set<string>(),
  parentProducts: new Set<string>(),
  partNumbers: new Set<string>(),
  vendors: new Set<string>(),
  booleans: {},
  // 1Y-online-tri: default to "both" so the filter is inactive on load.
  online: "both",
  newOrExisting: "both",
  mode: "orders",
  dateRange: DEFAULT_DATE_RANGE,
  viewLeft: "yoy",
  viewRight: "yoy",
  // AGENT-A7: FY-dropdown selections default to empty (no FY filter applied
  // until the user picks a year in the ViewSelectors FY dropdown).
  fyLeft: [],
  fyRight: [],
  // AGENT-BD: default to the main pivot visual.
  pivotVisualId: DEFAULT_PIVOT_VISUAL_ID,

  toggleCategory: (name) =>
    set((s) => ({ categories: toggleInSet(s.categories, name) })),

  toggleParent: (name) =>
    set((s) => ({ parentProducts: toggleInSet(s.parentProducts, name) })),

  togglePartNumber: (name) =>
    set((s) => ({ partNumbers: toggleInSet(s.partNumbers, name) })),

  toggleVendor: (name) =>
    set((s) => ({ vendors: toggleInSet(s.vendors, name) })),

  // 1Y-pp-filter: replace parent-product selection wholesale (used by the
  // multiselect's Select-All / Clear actions).
  setParentProducts: (names) =>
    set({ parentProducts: new Set(names) }),

  setBoolean: (key, value) =>
    set((s) => ({ booleans: { ...s.booleans, [key]: value } })),

  // 1Y-online-tri: drive the tri-state online filter. Stored outside the
  // booleans map; projected into `booleans.online` in toServerShape().
  setOnline: (v) => set({ online: v }),

  setNewOrExisting: (v) => set({ newOrExisting: v }),

  setMode: (m) => set({ mode: m }),

  setDateRange: (r) => set({ dateRange: r }),

  setView: (side, v) =>
    set(side === "left" ? { viewLeft: v } : { viewRight: v }),

  // AGENT-A7: FY-dropdown setters. Component-level wiring (mirroring into
  // `dateRange.years`) happens via the existing setDateRange action — this
  // setter only stores the per-side selection.
  setFyLeft: (years) => set({ fyLeft: [...years] }),
  setFyRight: (years) => set({ fyRight: [...years] }),

  // AGENT-BD: pivot visual override (ViewSelectors right card).
  setPivotVisualId: (id) => set({ pivotVisualId: id }),

  selectAllCategories: (names) =>
    set({ categories: new Set(names) }),

  clearCategories: () =>
    set({
      categories: new Set<string>(),
      parentProducts: new Set<string>(),
      partNumbers: new Set<string>(),
    }),

  reset: () =>
    set({
      categories: new Set<string>(),
      parentProducts: new Set<string>(),
      partNumbers: new Set<string>(),
      vendors: new Set<string>(),
      booleans: {},
      // 1Y-online-tri: reset online to inactive.
      online: "both",
      newOrExisting: "both",
      mode: "orders",
      dateRange: DEFAULT_DATE_RANGE,
      viewLeft: "yoy",
      viewRight: "yoy",
      // AGENT-A7: reset clears the per-side FY dropdown selections too.
      fyLeft: [],
      fyRight: [],
      // AGENT-BD: reset includes pivot visual.
      pivotVisualId: DEFAULT_PIVOT_VISUAL_ID,
    }),

  toServerShape: (): ServerFilterShape => {
    const s = get();
    // 1Y-online-tri: project the tri-state `online` value into the
    // booleans map for the wire. "yes"→true, "no"→false, "both"→omit.
    // The backend's `_BOOLEAN_FILTER_SPEC["online"]` already handles
    // true/false, so the API contract stays identical.
    const wireBooleans: Record<string, boolean> = { ...s.booleans };
    delete wireBooleans.online;
    if (s.online === "yes") wireBooleans.online = true;
    else if (s.online === "no") wireBooleans.online = false;
    // "both" → key absent → no filter applied.

    // AGENT-A4 / HSQ dynamic-window (2026-05-29): DATE RANGE.
    // - kind="fy": emit the FY years list verbatim (existing behaviour).
    // - kind="dynamic": drop any stashed FY years, but DO forward the custom
    //   From/To window when set. The backend's `_build_extra_filters` turns
    //   `from`/`to` into a `DATE >= / <=` predicate that scopes the pivot,
    //   charts, totals, and KPI cards to the selected calendar window. With
    //   no From/To set it's `{ kind:"dynamic", years:[] }` → no date filter,
    //   i.e. "show all periods" (the prior behaviour).
    const wireDateRange: DateRange =
      s.dateRange.kind === "dynamic"
        ? {
            kind: "dynamic",
            years: [],
            ...(s.dateRange.from ? { from: s.dateRange.from } : {}),
            ...(s.dateRange.to ? { to: s.dateRange.to } : {}),
          }
        : { kind: "fy", years: [...s.dateRange.years] };

    const shape: ServerFilterShape = {
      categories: Array.from(s.categories).sort(),
      parentProducts: Array.from(s.parentProducts).sort(),
      partNumbers: Array.from(s.partNumbers).sort(),
      vendors: Array.from(s.vendors).sort(),
      booleans: wireBooleans,
      newOrExisting: s.newOrExisting,
      mode: s.mode,
      dateRange: wireDateRange,
      viewLeft: s.viewLeft,
      viewRight: s.viewRight,
    };
    // AGENT-BD: only emit pivotVisualId when it differs from the default,
    // so a stock request body matches the legacy contract verbatim and
    // doesn't break older test fixtures.
    if (s.pivotVisualId && s.pivotVisualId !== DEFAULT_PIVOT_VISUAL_ID) {
      shape.pivotVisualId = s.pivotVisualId;
    }
    return shape;
  },
}));
